import { Student } from '../models/student.model';
import { Registration } from '../models/registration.model';
import { ContractStatus } from '../models/contract.model';
import {
  getStudentSchoolYear,
  isStudentInSchoolYear,
} from './student-school-year.utils';

/**
 * Get the current school year for a student based on their contract or registration
 */
export function getStudentCurrentSchoolYear(student: Student): number | undefined {
  return getStudentSchoolYear(student);
}

/**
 * Get the current status of a student based on their registration/contract state
 */
export function getStudentCurrentStatus(student: Student): 'no_registration' | 'registered' | 'contracted' {
  if (student.contract) {
    return 'contracted';
  }
  if (student.registration) {
    return 'registered';
  }
  return 'no_registration';
}

/**
 * Get the school ID for a student based on their registration
 */
export function getStudentSchoolId(student: Student): string | undefined {
  return student.startInfo.school.id || student.schoolId;
}

/**
 * Check if a student has a pending registration (new or waitlist)
 */
export function hasStudentPendingRegistration(student: Student): boolean {
  return student.registration?.status === 'new' || student.registration?.status === 'waitlist';
}

/**
 * Check if a student has an registration ready for contract (contractFinalization)
 */
export function isStudentReadyForContract(student: Student): boolean {
  return student.registration?.status === 'contractFinalization' && !student.contract;
}

/**
 * Check if a student has an active contract
 */
export function hasStudentActiveContract(student: Student): boolean {
  return student.contract?.status === 'active';
}

/**
 * Check if a student has a renewal pending
 */
export function hasStudentRenewalPending(student: Student): boolean {
  return student.contract?.status === 'renewalPending';
}

/**
 * Get display name for a student (lastName, firstName)
 */
export function getStudentDisplayName(student: Student): string {
  return `${student.lastName}, ${student.firstName}`;
}

/**
 * Get the reference number for a student's registration
 */
export function getStudentReferenceNumber(student: Student): string | undefined {
  return student.registration?.referenceNumber;
}

/**
 * Check if a student was created within the last N days
 */
export function isStudentRecentlyCreated(student: Student, days: number = 7): boolean {
  const daysAgo = new Date();
  daysAgo.setDate(daysAgo.getDate() - days);
  return student.createdOn.toDate() > daysAgo;
}

/**
 * Filter students by registration status
 */
export function filterStudentsByRegistrationStatus(
  students: Student[], 
  status: Registration['status']
): Student[] {
  return students.filter(student => student.registration?.status === status);
}

/**
 * Filter students by contract status
 */
export function filterStudentsByContractStatus(
  students: Student[], 
  status: ContractStatus
): Student[] {
  return students.filter(student => student.contract?.status === status);
}

/**
 * Filter students who need approval (new or waitlist registration)
 */
export function getStudentsNeedingApproval(students: Student[]): Student[] {
  return students.filter(student => 
    student.registration?.status === 'new' || student.registration?.status === 'waitlist'
  );
}

/**
 * Filter students who are ready for contract (contractFinalization but no contract)
 */
export function getStudentsReadyForContract(students: Student[]): Student[] {
  return students.filter(student => 
    student.registration?.status === 'contractFinalization' && !student.contract
  );
}

/**
 * Filter students by school year. Verlängerte Verträge zählen bis 31.07. auch
 * noch im alten Schuljahr (#1346).
 */
export function filterStudentsBySchoolYear(students: Student[], schoolYear: number): Student[] {
  return students.filter(student => isStudentInSchoolYear(student, schoolYear));
}

/**
 * Filter students by school ID
 */
export function filterStudentsBySchoolId(students: Student[], schoolId: string): Student[] {
  return students.filter(student => getStudentSchoolId(student) === schoolId);
}

/**
 * Search students by name (case insensitive)
 */
export function searchStudentsByName(students: Student[], searchTerm: string): Student[] {
  const lowerSearchTerm = searchTerm.toLowerCase();
  return students.filter(student =>
    student.firstName.toLowerCase().includes(lowerSearchTerm) ||
    student.lastName.toLowerCase().includes(lowerSearchTerm)
  );
}

/**
 * Search students by registration reference number
 */
export function searchStudentsByReferenceNumber(students: Student[], referenceNumber: string): Student[] {
  return students.filter(student =>
    student.registration?.referenceNumber.toLowerCase().includes(referenceNumber.toLowerCase())
  );
}

// Legacy function exports for backward compatibility during transition
export const isStudentReadyForEnrollment = isStudentReadyForContract;
export const hasStudentActiveEnrollment = hasStudentActiveContract;
export const filterStudentsByEnrollmentStatus = filterStudentsByContractStatus;
export const getStudentsReadyForEnrollment = getStudentsReadyForContract;